import { useEffect, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import ProductsTab from "@/components/inventory/ProductsTab";
import PurchasesTab from "@/components/inventory/PurchasesTab";
import LowStockAlertsTab from "@/components/inventory/LowStockAlertsTab";
import MarginsTab from "@/components/inventory/MarginsTab";
import ReportsTab from "@/components/inventory/ReportsTab";
import { INVENTORY } from "@/constants/testIds";

const EMPTY_SUMMARY = { total_inventory_value: 0, total_products: 0, low_stock_count: 0 };
const EMPTY_CONSUMPTION = { by_product: [], by_professional: [], by_service: [], by_month: [] };

export default function InventoryPage() {
  const [products, setProducts] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [margins, setMargins] = useState([]);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [consumption, setConsumption] = useState(EMPTY_CONSUMPTION);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const loadAll = async () => {
    setLoading(true);
    try {
      const [productsRes, lowStockRes, purchasesRes, marginsRes, summaryRes, consumptionRes] = await Promise.all([
        api.get("/inventory/products", { params: { active_only: false } }),
        api.get("/inventory/products/low-stock"),
        api.get("/inventory/purchases"),
        api.get("/inventory/margins"),
        api.get("/inventory/reports/summary"),
        api.get("/inventory/reports/consumption"),
      ]);
      setProducts(productsRes.data);
      setLowStock(lowStockRes.data);
      setPurchases(purchasesRes.data);
      setMargins(marginsRes.data);
      setSummary(summaryRes.data);
      setConsumption(consumptionRes.data);
    } catch (err) {
      toast({ title: "Error al cargar inventario", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const activeProducts = products.filter((p) => p.is_active);

  const handleCreateProduct = async (form) => {
    try {
      await api.post("/inventory/products", form);
      toast({ title: "Producto creado" });
      loadAll();
    } catch (err) {
      throw new Error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleUpdateProduct = async (id, form) => {
    try {
      await api.put(`/inventory/products/${id}`, form);
      toast({ title: "Producto actualizado" });
      loadAll();
    } catch (err) {
      throw new Error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleAdjustProduct = async (id, payload) => {
    try {
      await api.post(`/inventory/products/${id}/adjust`, payload);
      toast({ title: "Stock ajustado" });
      loadAll();
    } catch (err) {
      throw new Error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleDeactivateProduct = async (id) => {
    try {
      await api.delete(`/inventory/products/${id}`);
      toast({ title: "Producto desactivado" });
      loadAll();
    } catch (err) {
      toast({ title: "Error", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    }
  };

  const handleReactivateProduct = async (id) => {
    try {
      await api.put(`/inventory/products/${id}`, { is_active: true });
      toast({ title: "Producto reactivado" });
      loadAll();
    } catch (err) {
      toast({ title: "Error", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    }
  };

  const handleCreatePurchase = async (payload) => {
    try {
      await api.post("/inventory/purchases", payload);
      toast({ title: "Compra registrada", description: "El stock y el costo del producto se actualizaron." });
      loadAll();
    } catch (err) {
      throw new Error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid={INVENTORY.page}>
      <div>
        <h1 className="font-serif text-3xl sm:text-4xl tracking-tight text-foreground">Inventario</h1>
        <p className="text-base text-muted-foreground mt-1">Stock, compras, alertas y margenes de tu clínica.</p>
      </div>

      <Tabs defaultValue="products" className="w-full">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="products" data-testid={INVENTORY.tabProducts}>Productos</TabsTrigger>
          <TabsTrigger value="purchases" data-testid={INVENTORY.tabPurchases}>Compras</TabsTrigger>
          <TabsTrigger value="alerts" data-testid={INVENTORY.tabAlerts}>
            Alertas {lowStock.length > 0 && `(${lowStock.length})`}
          </TabsTrigger>
          <TabsTrigger value="margins" data-testid={INVENTORY.tabMargins}>Margenes</TabsTrigger>
          <TabsTrigger value="reports" data-testid={INVENTORY.tabReports}>Reportes</TabsTrigger>
        </TabsList>

        <TabsContent value="products" className="pt-6">
          <ProductsTab
            products={products} loading={loading}
            onCreate={handleCreateProduct} onUpdate={handleUpdateProduct}
            onAdjust={handleAdjustProduct} onDeactivate={handleDeactivateProduct} onReactivate={handleReactivateProduct}
          />
        </TabsContent>

        <TabsContent value="purchases" className="pt-6">
          <PurchasesTab purchases={purchases} products={activeProducts} loading={loading} onCreate={handleCreatePurchase} />
        </TabsContent>

        <TabsContent value="alerts" className="pt-6">
          <LowStockAlertsTab products={lowStock} loading={loading} />
        </TabsContent>

        <TabsContent value="margins" className="pt-6">
          <MarginsTab margins={margins} loading={loading} />
        </TabsContent>

        <TabsContent value="reports" className="pt-6">
          <ReportsTab summary={summary} consumption={consumption} loading={loading} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
